import React from 'react';
import { ActivityContentDiv, ActivityItem, ActivityTimeline, ProfileDiv, StatusIcon, ActivityTextDiv } from '../styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import Avatar from '../../../../component/avatar';
import { floatToHHMM, renderPriorityIconName, renderStatusIconName } from '../../../../helpers/common';
import { AVATAR_BORDER, COMMENT_TYPE, ICON_SIZE, PRIORITY_ICON_NAMES, STATUS_TYPE } from '../../../../global/constants';
import { TaskActivityLogInterface } from '../../../../mobx/interfaces/project';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../../../mobx/rootStore';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ActivityTimelineProps } from '../../../../interfaces/ProjectInterface';
dayjs.extend(relativeTime);

// Common function to get SVG icon name based on activity type
const getActivityIconName = (activity: TaskActivityLogInterface) => {
  const iconNameMap = {
    [COMMENT_TYPE.STATUS_ID_CHANGE]:
      typeof activity.priority === 'number'
        ? renderStatusIconName(activity.priority)
        : renderStatusIconName(STATUS_TYPE.UNSTARTED),
    [COMMENT_TYPE.CHANGE_PRIORITY]: renderPriorityIconName(activity.priority || 0),
    [COMMENT_TYPE.SET_MILESTONE]: 'milestone-icon',
    [COMMENT_TYPE.ADD_LABEL]: 'updated-label-icon',
    [COMMENT_TYPE.REMOVE_LABEL]: 'updated-label-icon',
    [COMMENT_TYPE.ESTIMATE_TIME]: 'time-icon',
    [COMMENT_TYPE.SET_DUE_DATE]: 'calendar-icon',
    [COMMENT_TYPE.CHANGE_DUE_DATE]: 'calendar-icon',
    [COMMENT_TYPE.REMOVE_DUE_DATE]: 'calendar-icon',
    [COMMENT_TYPE.UPDATE_TASK]: 'edit-icon',
    [COMMENT_TYPE.STATUS_CHANGE]: activity.status ? 'activity-completedtask-icon' : 'activity-reopentask-icon'
  };

  return iconNameMap[activity.type] || renderStatusIconName(STATUS_TYPE.UNSTARTED);
};

// Common function to get SVG icon viewBox based on activity type
const getActivityIconViewBox = (activity: TaskActivityLogInterface) => {
  const smallViewBoxTypes = [
    COMMENT_TYPE.CHANGE_PRIORITY,
    COMMENT_TYPE.STATUS_CHANGE,
    COMMENT_TYPE.STATUS_ID_CHANGE,
    COMMENT_TYPE.CHANGE_GROUP,
    COMMENT_TYPE.CREATE_TASK
  ];

  return smallViewBoxTypes.includes(activity.type) ? '0 0 16 16' : '0 0 18 18';
};

// Common function to get activity content based on type
const getActivityContent = (activity: TaskActivityLogInterface) => {
  const activityTypeContentMap = {
    [COMMENT_TYPE.STATUS_ID_CHANGE]: (
      <span>
        change status from {activity.previousGroup || ''} to {activity.group || ''}.
      </span>
    ),
    [COMMENT_TYPE.STATUS_CHANGE]: <span>{activity.status ? 'completed task.' : 're‑opened task.'}</span>,
    [COMMENT_TYPE.UPDATE_TASK]: <span>updated task. </span>,
    [COMMENT_TYPE.UNASSIGN]: <span>removed assignee.</span>,
    [COMMENT_TYPE.REMOVE_ASSIGNEE]: <span>unassigned task to {activity.assignee?.name || ''}.</span>,
    [COMMENT_TYPE.ESTIMATE_TIME]: (
      <span>
        set estimate time to{' '}
        {activity?.estimateTime !== 0 &&
          activity?.estimateTime !== null &&
          activity?.estimateTime !== undefined &&
          floatToHHMM(activity?.estimateTime)}
        hours.
      </span>
    ),
    [COMMENT_TYPE.CHANGE_GROUP]: (
      <span>
        change group from {activity.previousGroup || ''} to {activity.group || ''}.
      </span>
    ),
    [COMMENT_TYPE.ADD_LABEL]: <span>Added label {activity.group || ''}.</span>,
    [COMMENT_TYPE.REMOVE_LABEL]: <span>Removed label {activity.group || ''}.</span>,
    [COMMENT_TYPE.REMOVE_DUE_DATE]: <span>remove due date.</span>,
    [COMMENT_TYPE.CHANGE_DUE_DATE]: (
      <span>
        change due date from {activity.previousDueDate} to {activity.dueDate}.
      </span>
    ),
    [COMMENT_TYPE.SET_DUE_DATE]: <span>set due date to {activity.dueDate}.</span>,
    [COMMENT_TYPE.ADD_ASSIGNEE]: <span>assign task to {activity.assignee?.name || ''}.</span>,
    [COMMENT_TYPE.SET_MILESTONE]: <span>added task in milestone {activity.group || ''}.</span>,
    [COMMENT_TYPE.CREATE_TASK]: <span>created the task.</span>,
    [COMMENT_TYPE.CHANGE_PRIORITY]: (
      <span>
        change priority from {PRIORITY_ICON_NAMES[activity.previousPriority || 0]}
        {' to '}
        {PRIORITY_ICON_NAMES[activity.priority || 0]}.
      </span>
    )
  };

  return activityTypeContentMap[activity.type] || null;
};

const ActivityTimelineComponent: React.FC<ActivityTimelineProps> = ({ title, activities }) => {
  // mobx store variable
  const { appStore } = rootStore;
  const { themeMode } = appStore || {};

  if (activities.length === 0) return null;

  return (
    <ActivityTimeline>
      <p>{title}</p>
      <ActivityContentDiv>
        {activities.map((activity) => (
          <ActivityItem key={activity.id}>
            <ProfileDiv>
              {activity?.createdBy === 'Github' ? (
                <SVGIcon
                  name={themeMode?.theme === 'dark' ? 'github-inbox-dark-icon' : 'github-inbox-light-icon'}
                  width='26'
                  height='26'
                  viewBox={`0 0 ${ICON_SIZE.XXXL} ${ICON_SIZE.XXXL}`}
                  fill='var(--text-secondary)'
                />
              ) : (
                <Avatar
                  imgSrc={activity.user?.profile_photo || ''}
                  name={activity.user?.name || 'Unknown'}
                  size={26}
                  borderVariant={AVATAR_BORDER.NONE}
                />
              )}
              <StatusIcon>
                {activity.type === COMMENT_TYPE.REMOVE_ASSIGNEE ||
                activity.type === COMMENT_TYPE.UNASSIGN ||
                activity.type === COMMENT_TYPE.ADD_ASSIGNEE ? (
                  <Avatar
                    imgSrc={activity.assignee?.profile_photo || ''}
                    name={activity.assignee?.name || 'Unknown'}
                    size={26}
                    borderVariant={AVATAR_BORDER.NONE}
                  />
                ) : (
                  <SVGIcon
                    name={getActivityIconName(activity)}
                    width={ICON_SIZE.MD}
                    height={ICON_SIZE.MD}
                    viewBox={getActivityIconViewBox(activity)}
                    fill='var(--text-secondary)'
                  />
                )}
              </StatusIcon>
            </ProfileDiv>
            <ActivityTextDiv>
              <p>{activity.createdBy === 'Github' ? 'Github' : activity.user?.name}</p>
              <>
                {getActivityContent(activity)}
                <p>{activity.taskName}</p>
              </>
              <span>{dayjs(activity.updatedTime).fromNow()}</span>
            </ActivityTextDiv>
          </ActivityItem>
        ))}
      </ActivityContentDiv>
    </ActivityTimeline>
  );
};

export default observer(ActivityTimelineComponent);
